import { Fragment, useId, useState, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { categoryWithSub, formatDate, humanize, unmappedLabel, unmappedReasonLabel } from '../exportFormat';
import { categoryLabel, exportModeLabels, type ExportPreview, type SourceLine } from '../exportTypes';
import { NET_PAY_COPY, OPTION_WARNING_HELP, isLegacyOptions, optionBlockerHelp, optionBlockerTitle } from '../exportOptions';
import { blockerLink, journalTotals, previewIsExportable, totalsAgree, fixMappingUrl } from '../exportPreviewLogic';
import { formatCents, sumCents } from '../money';
import { BreakdownSections, ConfigurationBlock, LimitationsList, NetPayReconciliation, SummaryCounts } from './PreviewBreakdown';

const blockerTitle: Record<string, string> = {
  CodesWithoutMapping: 'Deduction codes without a mapping',
  UnmappedLine: 'Missing or unclear GL mapping',
  Unbalanced: 'Debits and credits differ',
  Incomplete: 'Payroll amounts not accounted for',
  PayrollNotApproved: 'Payroll is not finalized',
  PayrollSuperseded: 'Payroll was superseded',
  ConflictingExport: 'Conflicting export',
  SourceInconsistent: 'Payroll figures do not add up',
  SourceAmountPrecision: 'Amount has more than two decimals',
  ExcludedUnbalances: 'Excluded line would unbalance the entry',
  EmployeeDirectoryUnavailable: 'Employee details could not be read',
  PayrollAdjusted: 'Payroll was adjusted after approval',
  OtherModeExportExists: 'An export in the other mode already exists',
};

function SourceLinesTable({ lines, total, id }: { lines: SourceLine[]; total: number; id: string }) {
  return (
    <div className="ax-drill" id={id}>
      <div className="jr-table-scroll" role="region" aria-label="Payroll source lines for this journal line" tabIndex={0}>
        <table className="jr-table">
          <caption className="ax-caption">Payroll source lines behind this journal line</caption>
          <thead>
            <tr>
              <th scope="col">Category</th>
              <th scope="col">Employee</th>
              <th scope="col">Jurisdiction</th>
              <th scope="col">Side</th>
              <th scope="col" className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((s) => (
              <tr key={s.id}>
                <td>{categoryWithSub(s.category, s.subcategory, s.payrollCode)}{s.provenance === 'Correction' ? ' (correction)' : ''}</td>
                <td>{s.employeeReference ?? 'Not shown'}</td>
                <td>{s.jurisdiction ?? ''}</td>
                <td>{s.side}</td>
                <td className="num">{formatCents(s.amountCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><th scope="row" colSpan={4}>Total of these source lines</th><td className="num">{formatCents(total)}</td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function JournalTable({ preview }: { preview: ExportPreview }) {
  const uid = useId();
  // The group and the plain-words reason are shown only when the server sent them (a legacy export carries neither).
  const showResolution = (preview.journal?.lines ?? []).some((l) => l.groupLabel || l.resolutionReason);
  const extraColumns = showResolution ? 1 : 0;
  const [open, setOpen] = useState<Set<number>>(new Set());
  const journal = preview.journal!;
  const byId = new Map(preview.sourceLines.map((s) => [s.id, s]));
  const totals = journalTotals(journal.lines);
  const toggle = (n: number) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });

  return (
    <div className="jr-table-scroll" role="region" aria-label="Journal entry lines" tabIndex={0}>
      <table className="jr-table ax-journal" data-testid="journal-table">
        <caption className="ax-caption">
          Journal entry preview {journal.reference}: {journal.lines.length} line{journal.lines.length === 1 ? '' : 's'}, {preview.currency}, accounting date {formatDate(preview.accountingDate)}
        </caption>
        <thead>
          <tr>
            <th scope="col">Line</th>
            <th scope="col">GL account</th>
            <th scope="col" className="num">Debit</th>
            <th scope="col" className="num">Credit</th>
            <th scope="col">Description</th>
            <th scope="col">Source category</th>
            <th scope="col" className="num">Mapping version</th>
            {showResolution && <th scope="col">Group and why this account</th>}
            <th scope="col">Source lines</th>
          </tr>
        </thead>
        <tbody>
          {journal.lines.map((line) => {
            const parts = line.sourceLineIds.map((id) => byId.get(id)).filter((s): s is SourceLine => s !== undefined);
            const expanded = open.has(line.lineNumber);
            const panelId = `${uid}-src-${line.lineNumber}`;
            return (
              <Fragment key={line.lineNumber}>
              <tr>
                <td>{line.lineNumber}</td>
                <td><strong>{line.accountNumber}</strong> {line.accountName}</td>
                <td className="num">{line.side === 'Debit' ? formatCents(line.amountCents) : ''}</td>
                <td className="num">{line.side === 'Credit' ? formatCents(line.amountCents) : ''}</td>
                <td>{line.description}</td>
                <td>{categoryWithSub(line.category, line.subcategory, line.payrollCode)}</td>
                <td className="num">{line.mappingVersion}</td>
                {showResolution && (
                  <td data-testid={`line-resolution-${line.lineNumber}`}>
                    {line.groupLabel && <strong>{line.groupLabel}</strong>}{line.groupLabel && line.resolutionReason ? ': ' : ''}{line.resolutionReason ?? ''}
                  </td>
                )}
                <td>
                  <button
                    type="button"
                    className="jr-btn jr-btn--secondary jr-btn--small"
                    aria-expanded={expanded}
                    aria-controls={expanded ? panelId : undefined}
                    aria-label={`${expanded ? 'Hide' : 'Show'} ${line.sourceLineIds.length} payroll source line${line.sourceLineIds.length === 1 ? '' : 's'} for journal line ${line.lineNumber}`}
                    onClick={() => toggle(line.lineNumber)}
                  >
                    {expanded ? 'Hide' : 'Show'} ({line.sourceLineIds.length})
                  </button>
                </td>
              </tr>
              {expanded && (
                <tr className="ax-drill-row">
                  <td colSpan={8 + extraColumns}>
                    <SourceLinesTable id={panelId} lines={parts} total={sumCents(parts.map((p) => p.amountCents))} />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={2}>Total debits and credits</th>
            <td className="num" data-testid="total-debits">{formatCents(totals.debitsCents)}</td>
            <td className="num" data-testid="total-credits">{formatCents(totals.creditsCents)}</td>
            <td colSpan={4 + extraColumns}>Difference (debits less credits): <strong data-testid="difference">{formatCents(totals.debitsCents - totals.creditsCents)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ReconciliationPanel({ preview }: { preview: ExportPreview }) {
  const r = preview.reconciliation;
  if (!r) return null;
  const supplied = r.employerContributionsCents !== null;
  const rows: { label: string; value: string; note?: string }[] = [
    { label: 'Finalized gross earnings', value: formatCents(r.finalizedGrossCents) },
    { label: 'Employee taxes withheld', value: formatCents(r.employeeTaxesCents) },
    { label: 'Employer taxes', value: formatCents(r.employerTaxesCents) },
    { label: 'Pre-tax deductions', value: formatCents(r.preTaxDeductionsCents) },
    { label: 'Post-tax deductions', value: formatCents(r.postTaxDeductionsCents) },
    { label: 'Garnishments', value: formatCents(r.garnishmentsCents) },
    supplied
      ? { label: 'Employer contributions', value: formatCents(r.employerContributionsCents!) }
      : { label: 'Employer contributions', value: 'Not supplied by payroll', note: 'The payroll calculation does not supply employer benefit or retirement contributions.' },
    { label: 'Net pay', value: formatCents(r.netPayCents) },
    { label: 'Total payroll cost (gross plus employer taxes)', value: formatCents(r.totalPayrollCostCents) },
    { label: 'Journal debits', value: formatCents(r.journalDebitsCents) },
    { label: 'Journal credits', value: formatCents(r.journalCreditsCents) },
    { label: 'Unmapped amount', value: formatCents(r.unmappedAmountCents) },
    { label: 'Excluded amount', value: formatCents(r.excludedAmountCents) },
    { label: 'Reconciliation difference', value: formatCents(r.differenceCents), note: 'Source amounts less the amounts in the journal, the excluded list and the unmapped list. It must be 0.00.' },
    { label: 'Reconciliation complete', value: r.isComplete ? 'Yes' : 'No' },
  ];
  return (
    <section className="ax-block" aria-labelledby="ax-recon-title" data-testid="reconciliation">
      <h4 id="ax-recon-title">Reconciliation to the finalized payroll</h4>
      <div className="jr-table-scroll" role="region" aria-label="Reconciliation figures" tabIndex={0}>
        <table className="jr-table ax-recon">
          <caption className="ax-caption">Figures computed from the finalized payroll, compared with the journal ({preview.currency})</caption>
          <thead><tr><th scope="col">Figure</th><th scope="col" className="num">Amount</th></tr></thead>
          <tbody>
            {rows.map(({ label, value, note }) => (
              <tr key={label}>
                <th scope="row">{label}{note && <span className="jr-hint ax-note"> {note}</span>}</th>
                <td className="num">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CoveragePanel({ preview }: { preview: ExportPreview }) {
  if (preview.sourceCoverage.length === 0) return null;
  const glyph = { Supplied: '✔', PartiallySupplied: '◐', NotSupplied: '○' } as const;
  const word = { Supplied: 'Supplied', PartiallySupplied: 'Partly supplied', NotSupplied: 'Not supplied' } as const;
  return (
    <section className="ax-block" aria-labelledby="ax-coverage-title">
      <h4 id="ax-coverage-title">What the payroll calculation supplies</h4>
      <p className="jr-hint">A category that is not supplied is shown here, never dropped silently. It does not block the export by itself.</p>
      <div className="jr-table-scroll" role="region" aria-label="Payroll source coverage" tabIndex={0}>
        <table className="jr-table">
          <caption className="ax-caption">Payroll source coverage by category</caption>
          <thead><tr><th scope="col">Category</th><th scope="col">Status</th><th scope="col">Note</th></tr></thead>
          <tbody>
            {preview.sourceCoverage.map((c) => (
              <tr key={c.category}>
                <td>{categoryLabel(c.category)}</td>
                <td><span aria-hidden="true">{glyph[c.status]}</span> {word[c.status]}</td>
                <td>{c.status === 'NotSupplied' ? 'Not supplied by the payroll calculation.' : c.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Everything one preview report shows: the balanced/blocked summary, blockers and warnings, the included employees, the journal with a drill-down
 * from each line to its payroll source lines, the reconciliation panel, the unmapped and excluded tables and the source coverage. It renders a
 * server report as it is and never fills a gap: a blocked report shows its blockers and whatever partial lines the server sent.
 */
export function ExportPreviewView({ preview, runId, summaryRef }: { preview: ExportPreview; runId: string; summaryRef?: Ref<HTMLDivElement> }) {
  const exportable = previewIsExportable(preview);
  const journal = preview.journal;
  const agree = totalsAgree(preview);
  const hasNames = preview.employees.some((e) => e.displayName);

  return (
    <div className="ax-preview" data-testid="export-preview">
      {exportable && journal ? (
        <div ref={summaryRef} tabIndex={-1} role="status" className="jr-notice jr-notice--ok ax-summary" data-testid="preview-summary">
          <span aria-hidden="true">✔ </span>
          <strong>Balanced and complete.</strong> Total debits ({formatCents(journal.totalDebitsCents)}) equal total credits ({formatCents(journal.totalCreditsCents)}) and every payroll line is accounted for. Ready to export.
        </div>
      ) : (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="jr-summary ax-summary" data-testid="preview-summary">
          <p className="jr-summary__title"><span aria-hidden="true">✖ </span>Not ready to export</p>
          {preview.blockers.length > 0 ? (
            <ul>
              {preview.blockers.map((b, i) => {
                const help = optionBlockerHelp(b.code);
                const link = blockerLink(b.code, runId, preview.options);
                return (
                  <li key={`${b.code}-${i}`} data-blocker={b.code}>
                    <strong>{optionBlockerTitle(b.code) ?? blockerTitle[b.code] ?? humanize(b.code)}.</strong> {b.message}
                    {help && <span className="ax-help">{help}{link && <> <Link to={link.to}>{link.label}</Link></>}</span>}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>This preview cannot be exported as it stands.</p>
          )}
          {journal && !agree && <p><strong>The totals shown do not add up from the lines.</strong> Do not export; preview again.</p>}
        </div>
      )}

      {preview.warnings.length > 0 && (
        <div className="jr-warning" role="note">
          <p><strong>Check these mappings</strong> (they do not block the export):</p>
          <ul className="jr-plain-list">
            {preview.warnings.map((w, i) => {
              const help = OPTION_WARNING_HELP[w.code];
              const link = blockerLink(w.code, runId, preview.options);
              return (
                <li key={`${w.code}-${i}`} data-warning={w.code}>
                  {w.message}
                  {help && <span className="ax-help">{help}{link && <> <Link to={link.to}>{link.label}</Link></>}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ConfigurationBlock preview={preview} />
      <SummaryCounts preview={preview} />

      {preview.unmapped.length > 0 && (
        <section className="ax-block" aria-labelledby="ax-unmapped-title" data-testid="unmapped">
          <h4 id="ax-unmapped-title">Payroll lines with no usable GL mapping</h4>
          <div className="jr-table-scroll" role="region" aria-label="Unmapped payroll lines" tabIndex={0}>
            <table className="jr-table">
              <caption className="ax-caption">Unmapped payroll lines ({preview.unmapped.length})</caption>
              <thead>
                <tr><th scope="col">Payroll item</th><th scope="col">Why</th><th scope="col" className="num">Lines</th><th scope="col" className="num">Amount</th><th scope="col">Fix</th></tr>
              </thead>
              <tbody>
                {preview.unmapped.map((u, i) => (
                  <tr key={`${u.category}-${u.subcategory}-${u.payrollCode}-${i}`}>
                    <td>{unmappedLabel(u)}</td>
                    <td>{unmappedReasonLabel[u.reason] ?? u.reason}</td>
                    <td className="num">{u.lineCount}</td>
                    <td className="num">{formatCents(u.amountCents)}</td>
                    <td><Link to={fixMappingUrl(runId, u)} aria-label={`Fix the mapping for ${unmappedLabel(u)}`}>Fix mapping</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {preview.excluded.length > 0 && (
        <section className="ax-block" aria-labelledby="ax-excluded-title" data-testid="excluded">
          <h4 id="ax-excluded-title">Payroll lines excluded from the journal</h4>
          <p className="jr-hint">These follow a recorded non-posting rule. They are counted in the excluded amount and never silently dropped.</p>
          <div className="jr-table-scroll" role="region" aria-label="Excluded payroll lines" tabIndex={0}>
            <table className="jr-table">
              <caption className="ax-caption">Excluded payroll lines ({preview.excluded.length})</caption>
              <thead><tr><th scope="col">Category</th><th scope="col">Reason</th><th scope="col" className="num">Amount</th></tr></thead>
              <tbody>
                {preview.excluded.map((x, i) => (
                  <tr key={`${x.mappingId}-${i}`}><td>{categoryLabel(x.category)}</td><td>{x.reason}</td><td className="num">{formatCents(x.amountCents)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {preview.employees.length > 0 && (
        <section className="ax-block" aria-labelledby="ax-employees-title">
          <h4 id="ax-employees-title">Employees included</h4>
          <p className="jr-hint">
            The export always covers the whole payroll; you cannot pick employees. {hasNames ? '' : 'Names are shown only to people who can view per-employee detail.'}
          </p>
          <div className="jr-table-scroll" role="region" aria-label="Employees included in this export" tabIndex={0}>
            <table className="jr-table" data-testid="employees-table">
              <caption className="ax-caption">Employees included in this export ({preview.employees.length})</caption>
              <thead>
                <tr>
                  <th scope="col">Employee</th>
                  {hasNames && <th scope="col">Name</th>}
                  <th scope="col">Department</th>
                  <th scope="col">Location</th>
                  <th scope="col" className="num">Gross pay</th>
                  <th scope="col" className="num">Net pay</th>
                </tr>
              </thead>
              <tbody>
                {preview.employees.map((e) => (
                  <tr key={e.employeeId}>
                    <td>{e.employeeReference ?? 'Not shown'}</td>
                    {hasNames && <td>{e.displayName ?? ''}</td>}
                    <td>{e.department ?? ''}</td>
                    <td>{e.location ?? ''}</td>
                    <td className="num">{formatCents(e.grossEarningsCents)}</td>
                    <td className="num">{formatCents(e.netPayCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={hasNames ? 4 : 3}>Total</th>
                  <td className="num">{formatCents(sumCents(preview.employees.map((e) => e.grossEarningsCents)))}</td>
                  <td className="num">{formatCents(sumCents(preview.employees.map((e) => e.netPayCents)))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      {journal && journal.lines.length > 0 && (
        <section className="ax-block" aria-labelledby="ax-journal-title">
          <h4 id="ax-journal-title">Journal entry preview ({exportModeLabels[preview.mode]})</h4>
          <p className="jr-hint">
            {preview.mode === 'PerEmployee'
              ? 'Per employee: each employee\'s lines are kept apart, with one net pay credit per employee to Payroll Clearing.'
              : !isLegacyOptions(preview.options) && preview.options.netPayDetailMode
                ? `${NET_PAY_COPY[preview.options.netPayDetailMode]} Every other line stays consolidated. Employee detail for any line is available through its source lines.`
                : 'Compound entry: one summarized net pay credit to Payroll Clearing. Employee detail stays in EPN: open a line\'s source lines to see it.'}
          </p>
          <JournalTable preview={preview} />
        </section>
      )}

      <BreakdownSections preview={preview} />
      <NetPayReconciliation preview={preview} />
      <ReconciliationPanel preview={preview} />
      <CoveragePanel preview={preview} />
      <LimitationsList preview={preview} />
    </div>
  );
}
